import styles from './style.postcss';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Map, Set } from 'immutable';
import is from 'is_js';
import {
  log,
  connect,
  testClass,
  PageCard,
  Form,
  Button,
  Checkbox,
  Icon,
} from 'omni-common-ui';
import { PersonalisedHomework } from 'data/OnlineHomework/Personalised';
import HomeworkType from 'data/Homework/Types';
import ScrollPageToHereNow from 'components/ScrollPageToHereNow';
import HomeworkContentItemForm from './HomeworkContentItemForm';
import StudentPicker from './StudentPicker';

const MAX_CONTENT_ENTRIES_PER_HOMEWORK = 10;

class PersonalisedHomeworkFormCard extends Component {
  constructor() {
    super();
    this.state = {
      // this model is set when there are local (unsaved) changes
      // otherwise the model in props is used instead
      model: undefined,
    };
    this._onModelChanged = this._onModelChanged.bind(this);
    this._getConsumedContentTypes = this._getConsumedContentTypes.bind(this);
    this._onAddNewHomeworkContentClicked = this._onAddNewHomeworkContentClicked.bind(this);
    this._onStudentCheckedOrUnchecked = this._onStudentCheckedOrUnchecked.bind(this);
  }

  componentWillReceiveProps({ model: newModel, onNotSaveable }) {
    if (this._getCurrentModel().id !== newModel.id) {
      // wipe the local model
      this.setState({ model: undefined }, () => {
        // signal that the form is no longer saveable to permit unsaved changes nav.
        onNotSaveable && onNotSaveable();
      });
    }
  }

  componentWillUpdate(nextProps, nextState) {
    const { onNowSaveable, onNotSaveable } = this.props;
    const isSaveable = this._isFormSaveableWithState(nextState);
    const isSaveablePrev = this._isFormSaveableWithState(this.state);
    if (isSaveable && ! isSaveablePrev) {
      onNowSaveable && onNowSaveable();
    } else if (! isSaveable && isSaveablePrev) {
      onNotSaveable && onNotSaveable();
    }
  }

  forceSubmit(extraParam) {
    // heh. called by the unsaved changes warning dialog when "save" is pushed.
    const { onSubmit } = this.props;
    if (! onSubmit) return;
    onSubmit(this._getCurrentModel(), extraParam);
  }

  _getCurrentModel(state = this.state) {
    return state.model || this.props.model;
  }

  _getConsumedContentTypes() {
    return this._getCurrentModel().contents.map((item) => item.homeworkTypeId);
  }

  _isFormSaveableWithState(state = this.state) {
    // use local form state (set when there are changes)
    const isValid = PersonalisedHomework.validate(state.model);
    if (! isValid) {
      log.info('PersonalisedHomework form model is not valid. model:', state.model);
    }
    return isValid;
  }

  _isAddHomeworkContentAllowed() {
    const { homeworkContentTypes } = this.props;
    const currentCount = this._getCurrentModel().contents.size;
    return currentCount < MAX_CONTENT_ENTRIES_PER_HOMEWORK &&
        this._getConsumedContentTypes().size < homeworkContentTypes.size;
  }

  _onAddNewHomeworkContentClicked() {
    const newModel = this._getCurrentModel().update('contents', (contents) =>
        contents.push(new PersonalisedHomework.Item()));
    this.setState({ model: newModel });
  }

  _onStudentCheckedOrUnchecked(studentId, isChecked) {
    const checkedStudentIds = this._getCurrentModel().studentIds;
    const newSet = isChecked ?
      checkedStudentIds.add(studentId) :
      checkedStudentIds.delete(studentId);
    const newModel = this._getCurrentModel().set('studentIds', newSet);
    this.setState({ model: newModel });
  }

  _onAllStudentsCheckedOrUnchecked(isChecked) {
    const { students } = this.props;
    let newSet;
    if (isChecked) {
      newSet = new Set(students.map((student) => student.studentGeneralInfo.id));
    } else {
      newSet = new Set();
    }
    const newModel = this._getCurrentModel().set('studentIds', newSet);
    this.setState({ model: newModel });
  }

  _onModelChanged(model) {
    this.setState({ model });
  }

  render() {
    const {
      students,
      onSubmit,
      onDiscard,
      overrideHeadingText,
      courseTypeLevelId,
      homeworkContentTypes,
    } = this.props;
    const model = this._getCurrentModel();  // DO NOT use props.model in render() !!
    let headingText = model.id ? `Personalised Practice ${model.id}` : 'Personalised Practice';
    if (is.string(overrideHeadingText)) {
      headingText = overrideHeadingText;
    }
    const labelClassName = classnames(testClass('personalised-homework-form-checkbox-all'),
      styles.PersonalisedHomeworkFormCard_allStudentsCheckbox);
    return <PageCard>
      <PageCard.Heading text={headingText} />
      <PageCard.Content className={styles.PersonalisedHomeworkFormCard}>
        <ScrollPageToHereNow offset={- 250} />
        <Form className={styles.PersonalisedHomeworkFormCard_form}
            onSubmit={() => {
              if (! onSubmit) return;
              onSubmit(this._getCurrentModel());
            }}>
          {this._getCurrentModel().contents.map((item, idx, items) =>
            <HomeworkContentItemForm key={item.homeworkTypeId}
                courseTypeLevelId={courseTypeLevelId}
                homeworkContentTypes={homeworkContentTypes}
                contentItem={item}
                contentItemIndex={idx}
                contentItemCount={items.size}
                model={this._getCurrentModel()}
                onModelChange={this._onModelChanged} />
          )}
          {this._isAddHomeworkContentAllowed() ? <p className={
              styles.PersonalisedHomeworkFormCard_addLink}>
            <a onClick={this._onAddNewHomeworkContentClicked}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}>
              <Icon id="new-message" /> Add Practice
            </a>
          </p> : null}
          <h4>Assigned to</h4>
          {students.size ? <label className={labelClassName}
              htmlFor="PersonalisedHomeworkFormCard-checkbox-all">
            <Checkbox id="PersonalisedHomeworkFormCard-checkbox-all"
                value="all"
                checked={students.size === model.studentIds.size}
                onChange={(isChecked) => {
                  this._onAllStudentsCheckedOrUnchecked(isChecked);
                }} />
            <span className={
                styles.PersonalisedHomeworkFormCard_allStudentsCheckbox_labelText}>
              All Students
            </span>
          </label> : <p>There are no students available for assignment.</p>}
          <StudentPicker students={students}
              selectedStudentIds={model.studentIds}
              onStudentCheckedOrUnchecked={this._onStudentCheckedOrUnchecked} />
          <Button.Container className={styles.PersonalisedHomeworkFormCard_buttons}
              align="right">
            <Button type={Button.Type.primary}
                attrs={{ type: 'submit' }}
                className={testClass('personalised-homework-form-save')}
                disabled={! this._isFormSaveableWithState()}>
              Save
            </Button>
            <Button type={Button.Type.default}
                className={testClass('personalised-homework-form-discard')}
                onClick={onDiscard ? (ev) => {
                  ev.preventDefault();
                  onDiscard();
                } : null}>
              Discard
            </Button>
          </Button.Container>
        </Form>
      </PageCard.Content>
    </PageCard>;
  }
}

PersonalisedHomeworkFormCard.propTypes = {
  model: PropTypes.instanceOf(PersonalisedHomework).isRequired,
  overrideHeadingText: PropTypes.string,
  students: PropTypes.object.isRequired,
  courseTypeLevelId: PropTypes.string,
  homeworkContentTypes: PropTypes.instanceOf(Map).isRequired,

  // form lifecycle callbacks
  onSubmit: PropTypes.func,
  onDiscard: PropTypes.func,

  // these two are for toggling save via the unsaved changes warning dialog
  onNowSaveable: PropTypes.func,
  onNotSaveable: PropTypes.func,
};

function mapStateToProps(state, { courseTypeLevelId }) {
  return {
    homeworkContentTypes:
      HomeworkType.accessors.getTypesByCourseTypeLevelId(state, courseTypeLevelId),
  };
}

export default connect(mapStateToProps, null, null, { withRef: true })(  // allow getWrappedInstance
    PersonalisedHomeworkFormCard);



// WEBPACK FOOTER //
// ./containers/Group/AssignHomework/Assignments/PersonalisedHomeworkFormCard/index.jsx